import { useState, useEffect, useRef } from 'react'
import { router } from '@inertiajs/react'
import { Rock } from '../../types'
import RockPercentSlider from './RockPercentSlider'

interface Props {
  rock: Rock
  teamId: number
  meetingId?: number
  onClose: () => void
  notePlaceholder?: string
  compact?: boolean
  /** Called shortly after a successful save (after the brief "Saved ✓" cue). */
  onSaved?: () => void
}

export default function RockCheckInForm({ rock, teamId, meetingId, onClose, notePlaceholder, compact, onSaved }: Props) {
  // Round to a whole number: stored percent can carry fractional values
  // (e.g. computed from a target) that fail the backend's 0.5-increment
  // validation if saved untouched. The slider itself works in whole numbers.
  const [percent, setPercent] = useState<number>(Math.round(rock.percent_complete ?? 0))
  const [note, setNote] = useState('')
  const [value, setValue] = useState(rock.current_value?.toString() || '')
  const [processing, setProcessing] = useState(false)
  const [saved, setSaved] = useState(false)
  const savedTimer = useRef<number | undefined>(undefined)

  useEffect(() => () => {
    if (savedTimer.current) window.clearTimeout(savedTimer.current)
  }, [])

  const handleSubmit = () => {
    if (processing) return
    setProcessing(true)
    setSaved(false)
    router.post(`/teams/${teamId}/rocks/${rock.id}/check_in`, {
      percent_complete: percent,
      note: note || null,
      value: rock.progress_type === 'status_with_target' && value ? parseFloat(value) : null,
      meeting_id: meetingId || null,
    }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        setSaved(true)
        if (savedTimer.current) window.clearTimeout(savedTimer.current)
        if (onSaved) {
          // Show the "Saved ✓" cue briefly, then let the parent advance
          // to the next rock (or close the modal on the last one).
          savedTimer.current = window.setTimeout(() => {
            setSaved(false)
            onSaved()
          }, 800)
        } else {
          savedTimer.current = window.setTimeout(() => setSaved(false), 2000)
        }
      },
      onFinish: () => {
        setProcessing(false)
      },
    })
  }

  const saveLabel = (full: string) => (processing ? 'Saving…' : saved ? 'Saved ✓' : full)

  const defaultPlaceholder = "What did you do last week to move this forward? What will you do this week to continue to move this forward?"

  if (compact) {
    return (
      <div>
        <div className="mb-3">
          <label className="block text-sm font-medium text-text-secondary mb-1.5">Percent Complete</label>
          <RockPercentSlider value={percent} onChange={setPercent} size="sm" />
        </div>

        {rock.progress_type === 'status_with_target' && (
          <div className="mb-3">
            <label className="block text-sm font-medium text-text-secondary mb-1">Current Value</label>
            <input
              type="number"
              value={value}
              onChange={e => setValue(e.target.value)}
              className="w-full px-3 py-1.5 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
              placeholder={rock.target_type === 'currency' ? '$0' : '0'}
            />
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm font-medium text-text-secondary mb-1">Note (optional)</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            className="w-full px-3 py-1.5 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 resize-none"
            placeholder={notePlaceholder ?? defaultPlaceholder}
          />
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-1.5 px-3 border border-stone/30 rounded-lg text-sm text-text-secondary hover:bg-[#F8F9FA]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            aria-disabled={processing}
            className={`flex-1 py-1.5 px-3 bg-pinnacle-red text-white rounded-lg text-sm font-medium hover:bg-pinnacle-red/90 ${processing ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {saveLabel('Save')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">Percent Complete</label>
        <RockPercentSlider value={percent} onChange={setPercent} />
      </div>

      {rock.progress_type === 'status_with_target' && (
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Current Value
            {rock.target_value !== null && (
              <span className="text-text-muted font-normal"> (Target: {rock.target_type === 'currency' ? '$' : ''}{rock.target_value}{rock.target_type === 'percentage' ? '%' : ''})</span>
            )}
          </label>
          <input
            type="number"
            value={value}
            onChange={e => setValue(e.target.value)}
            className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1">Note (optional)</label>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red resize-none"
          placeholder={notePlaceholder ?? defaultPlaceholder}
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-2 px-4 border border-stone/30 rounded-lg text-sm text-text-secondary hover:bg-[#F8F9FA]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          aria-disabled={processing}
          className={`flex-1 py-2 px-4 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90 ${processing ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {saveLabel('Save Check-In')}
        </button>
      </div>
    </div>
  )
}
